/**
 * useInfiniteMessages - Infinite Scroll Hook para Mensagens
 * Implementa carregamento incremental com paginação reversa
 * Mensagens mais antigas são carregadas ao rolar para o TOPO
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { chatService } from '../services';
import type { ConversationMessageQuery } from '@troia-v3/types';

export interface UseInfiniteMessagesOptions {
  /** ID da conversa */
  conversationId: string;
  /** Limite de itens por página */
  limit?: number;
  /** Habilitar/desabilitar query */
  enabled?: boolean;
  /** Filtros adicionais */
  additionalFilters?: Omit<ConversationMessageQuery, 'conversationId' | 'page' | 'limit'>;
}

/**
 * Hook para infinite scroll de mensagens com paginação reversa
 *
 * ⚠️ IMPORTANTE: Scroll reverso para UX de chat
 * - Primeira página: Mensagens mais RECENTES
 * - Próximas páginas: Mensagens mais ANTIGAS (ao rolar para cima)
 * - Ordem: Mais antiga → Mais recente (ascendente por sentAt)
 *
 * @example
 * ```tsx
 * const {
 *   data,
 *   fetchNextPage,
 *   hasNextPage,
 *   isFetchingNextPage,
 *   isLoading
 * } = useInfiniteMessages({
 *   conversationId: 'conv-123',
 *   limit: 50
 * });
 *
 * // Todas as mensagens flat (oldest first)
 * const messages = data?.pages.flatMap(page => page.items) ?? [];
 * ```
 */
export const useInfiniteMessages = (options: UseInfiniteMessagesOptions) => {
  const {
    conversationId,
    limit = 70, // Limite otimizado para performance e UX
    enabled = true,
    additionalFilters = {}
  } = options;

  return useInfiniteQuery({
    queryKey: ['messages', 'infinite', conversationId, additionalFilters],
    queryFn: async ({ pageParam, signal }) => {
      // ⚠️ B8 (review 2026-07-27): `sortBy`/`sortOrder` NÃO existem mais aqui —
      // eram dead code. `chatService.getMessages` (chatService.ts) nunca
      // serializou esses dois campos na querystring (só conversationId/page/
      // limit/filters), então nunca chegavam ao backend. O comentário antigo
      // ("Backend retorna oldest first") também estava errado: o default do
      // backend, sem `sortBy`, é `sentAt: -1` (NEWEST first) — ver
      // `conversation-messages/repository.ts` `findMany`. Se algum dia alguém
      // "consertar" o gap e passar a serializar esses campos, `sortOrder: 'asc'`
      // INVERTERIA a ordem de paginação (página 1 passaria a ser a mais ANTIGA,
      // não a mais recente) e quebraria a reconciliação de `lastMessage` — por
      // isso removidos, não só comentados.
      const query: ConversationMessageQuery = {
        conversationId,
        page: pageParam as number,
        limit,
        ...additionalFilters
      };

      const response = await chatService.getMessages(query, signal);

      // ✅ Cada página vem NEWEST first (default do backend). `ChatBox.tsx`
      // flatMap + `.reverse()` no consumo é quem produz a ordem oldest-first
      // exibida na tela — não este hook.
      return response;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: any) => {
      // Se há mais páginas, retorna próximo número
      if (lastPage.page < lastPage.totalPages) {
        return lastPage.page + 1;
      }
      // Sem mais páginas
      return undefined;
    },
    staleTime: 10 * 1000, // 10 seconds (mensagens mudam rápido)
    gcTime: 5 * 60 * 1000, // 5 minutes
    enabled: enabled && !!conversationId,
    refetchOnMount: false,
    refetchOnWindowFocus: true // ✅ BUGFIX: Habilitar para refetch ao voltar de background (BUGFIX-BACKGROUND-MESSAGES.md)
  });
};
